import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";

import { AllCommunityModule, type StateUpdatedEvent } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Box, Button, Input, Tab, Tabs, TextField } from "@mui/material";
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from "@mui/material";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode, { type Code } from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";

import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import { useSettingsStore } from "@user/store/settings_store";

import "./popup_stock_history.scoped.scss";

interface State {
  tabValue: number
}

const defState: State = {
  tabValue: 0
};

interface SearchParams {
  skuNo: string
}

const defSearchParams: SearchParams = {
  skuNo: ""
};

interface Result {
  info: any,
  skuStockUpdHistInfos: any[],
  skuUpdHistInfos: any[]
}

const defResult = {
  info: {},
  skuStockUpdHistInfos: [],
  skuUpdHistInfos: []
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const CustomTabPanel = React.memo(
  (props: TabPanelProps) => {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`tabpanel-${index}`}
        aria-labelledby={`tab-${index}`}
        className="tab-contents"
      >
        {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
      </div>
    );
  }
);

function PopupStockHistory(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [result, setResult] = useState<Result>(kutil.deepCopy(defResult));

  const gridStateKey4stock = "/stock/popup_stock_history.stock";
  const gridState4stock = useMemo(() => useSettingsStore.getState().getGridState(gridStateKey4stock), []);
  const gridStateKey4sku = "/stock/popup_stock_history.sku";
  const gridState4sku = useMemo(() => useSettingsStore.getState().getGridState(gridStateKey4sku), []);

  const searchParamsRef = useRef(searchParams);
  const updateSearchParams = useCallback((patch: Partial<SearchParams>, isReset: boolean = false) => {
    if (isReset) {
      searchParamsRef.current = { ...patch } as SearchParams;
    } else {
      searchParamsRef.current = { ...searchParamsRef.current, ...patch };
    }
    setSearchParams(searchParamsRef.current);
    return searchParamsRef.current;
  }, []);

  const gridInfo4stock = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "작업일시", field: "regDate", width: 200, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.date("yyyy-MM-dd HH:mm:ss") },
    { headerName: "작업구분", field: "skuStockUpdType", width: 100, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("skuStockUpdType") },
    { headerName: "SKU No", field: "skuNoEx", width: 100, cellStyle: {textAlign: "center"} },
    { headerName: "Work Qty", headerClass: "hd-ta-right", field: "", width: 110, cellStyle: {textAlign: "right"},
      valueGetter: (p: any) => {
        let workQty = 0;
        if (p.data.skuStockUpdType == "O") {
          workQty = Number(p.data.updBeforeStockQty) - Number(p.data.updAfterStockQty);
        } else {
          workQty = Number(p.data.updAfterStockQty) - Number(p.data.updBeforeStockQty);
        }
        return valueFormatter.number(workQty);
      },
    },
    { headerName: "Stock Qty", headerClass: "hd-ta-right", field: "updAfterStockQty", width: 110, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number },
    { headerName: "창고", field: "wh", width: 100, cellStyle: { textAlign: "center"}, valueFormatter: valueFormatter.commonCode("wh") },
    { headerName: "P/N", headerClass: "hd-ta-left", field: "pn", width: 150, cellStyle: { textAlign: "left"} },
    { headerName: "TR No", headerClass: "hd-ta-left", field: "trNo", width: 150, cellStyle: { textAlign: "left"} },
    { headerName: "S/N", headerClass: "hd-ta-left", field: "sn", width: 150, cellStyle: { textAlign: "left"} },
    { headerName: "작업자", field: "regName", width: 100, cellStyle: {textAlign: "center"} }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridCallbacks4stock = {
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, gridStateKey4stock);
    },
  };

  const gridInfo4sku = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "작업일시", field: "regDate", width: 200, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.date("yyyy-MM-dd HH:mm:ss") },
    { headerName: "SKU No", field: "skuNoEx", width: 100, cellStyle: {textAlign: "center"} },
    { headerName: "SKU Type", field: "skuType", width: 100, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("skuType") },
    { headerName: "SKU Size", field: "skuSize", width: 100, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("skuSize") },
    { headerName: "변경전 Cell", field: "updBeforeValue", width: 150, cellStyle: {textAlign: "center"} },
    { headerName: "변경후 Cell", field: "updAfterValue", width: 150, cellStyle: {textAlign: "center"} },
    { headerName: "수정자", headerClass: "hd-ta-left", field: "regName", width: 150, cellStyle: {textAlign: "left"} }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridCallbacks4sku = {
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, gridStateKey4sku);
    },
  };

  useEffect(() => {
    init();
  }, []);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await doSearch();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    const searchParams = kutil.deepCopy(defSearchParams);
    searchParams.skuNo = props.data.skuNo;
    updateSearchParams(searchParams);
  }

  async function doSearch() {
    const searchParams = searchParamsRef.current;

    try {
      const reqSearchParams = kutil.copyObj(searchParams, {} as SearchParams);
      const data = await kutil.sendGet("/api/stock/stock_status.data?mode=searchDetail", reqSearchParams, {});
      setResult({
        info: data.info,
        skuStockUpdHistInfos: data.skuStockUpdHistInfos,
        skuUpdHistInfos: data.skuUpdHistInfos,
      });
    } catch(e) {
      console.log(e);
    }
  }

  async function closePopup() {
    props.onClose();
  }

  return (
    <>
      {/* 팝업 */}
      <Dialog
        sx={{ "& .MuiDialog-container": { "& .MuiPaper-root": { width: "100%", maxWidth: "1300px", }, }, }}
        aria-labelledby="customized-dialog-title"
        open={props.isShow}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          재고 이력
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={closePopup}
          sx={(theme) => ({
            position: "absolute",
            right: 36,
            top: 24,
            color: theme.palette.grey[500],
          })}
        >
        </IconButton>
        <DialogContent>
          {/* 팝업 내용 */}
          <div className="popup-contents">
            {/* 탭 */}
            <Box sx={{ width: "100%" }}>
              <div className="tab-area">
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <Tabs value={state.tabValue} aria-label="basic tabs example" onChange={(_e, newValue) => {setState(prev => ({ ...prev, tabValue: newValue }))}}>
                    <Tab disableRipple label="재고 이력" />
                    <Tab disableRipple label="SKU 셀수정 이력" />
                  </Tabs>
                </Box>
              </div>
              {/* 재고 이력 */}
              <CustomTabPanel value={state.tabValue} index={0}>
                <AgGridProvider modules={modules}>
                  <div className="grid-area">
                    <CustomAgGridReact
                      columnDefs={gridInfo4stock}
                      rowData={result.skuStockUpdHistInfos}
                      defaultColDef={kutil.defaultColDef}
                      initialState={gridState4stock}
                      showInfo={false}
                      onStateUpdated={gridCallbacks4stock.onStateUpdated}
                    />
                  </div>
                </AgGridProvider>
              </CustomTabPanel>
              {/* SKU 셀수정 이력 */}
              <CustomTabPanel value={state.tabValue} index={1}>
                <AgGridProvider modules={modules}>
                  <div className="grid-area">
                    <CustomAgGridReact
                      columnDefs={gridInfo4sku}
                      rowData={result.skuUpdHistInfos}
                      defaultColDef={kutil.defaultColDef}
                      initialState={gridState4sku}
                      showInfo={false}
                      onStateUpdated={gridCallbacks4sku.onStateUpdated}
                    />
                  </div>
                </AgGridProvider>
              </CustomTabPanel>
            </Box>
          </div>
          {/*// 팝업 내용 */}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" autoFocus onClick={closePopup}>
            닫기
          </Button>
        </DialogActions>
      </Dialog>
      {/*// 팝업 */}
    </>
  );
}

export default React.memo(PopupStockHistory);
